import type {
  DestinoDeUploadAssinado,
  OpcoesDoUploadAssinado,
} from '@/shared/types/uploadAssinado';

const TEMPO_MAXIMO_SEM_PROGRESSO_MS = 60_000;

/** Falha do PUT em si (storage recusou ou a conexão caiu): a mensagem já é para o usuário. */
export class FalhaNoUploadAssinado extends Error {
  readonly status: number;

  constructor(mensagem: string, status: number) {
    super(mensagem);
    this.name = 'FalhaNoUploadAssinado';
    this.status = status;
  }
}

/**
 * PUT do arquivo na URL assinada, via XHR pelo progresso (o `fetch` não informa progresso de
 * upload) e pelo abort.
 *
 * Os headers vêm da API e são enviados como estão: eles entram na assinatura, e qualquer
 * divergência faz o storage recusar o objeto. Nada de cookie nem `Authorization`: a autorização
 * está na assinatura da URL.
 */
export const uploadParaUrlAssinada = (
  destino: DestinoDeUploadAssinado,
  arquivo: Blob,
  { onProgress, signal }: OpcoesDoUploadAssinado = {},
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const xhr = new XMLHttpRequest();
    let prazo: ReturnType<typeof setTimeout> | undefined;
    const abortar = () => xhr.abort();
    const terminar = () => {
      clearTimeout(prazo);
      signal?.removeEventListener('abort', abortar);
    };
    const expirar = () => {
      terminar();
      reject(new FalhaNoUploadAssinado('O envio ficou sem progresso. Tente novamente.', 0));
      xhr.abort();
    };
    const renovarPrazo = () => {
      clearTimeout(prazo);
      // Um arquivo grande pode demorar; o limite vale apenas para a conexão sem progresso.
      prazo = setTimeout(expirar, TEMPO_MAXIMO_SEM_PROGRESSO_MS);
    };

    signal?.addEventListener('abort', abortar, { once: true });

    xhr.upload.onprogress = (event) => {
      renovarPrazo();
      if (event.lengthComputable && event.total > 0) {
        onProgress?.(Math.min(99, Math.round((event.loaded / event.total) * 100)));
      }
    };

    xhr.onload = () => {
      terminar();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new FalhaNoUploadAssinado(
          'Não foi possível enviar o arquivo. Tente novamente.',
          xhr.status,
        ),
      );
    };

    xhr.onerror = () => {
      terminar();
      reject(new FalhaNoUploadAssinado('Falha de conexão ao enviar o arquivo.', 0));
    };
    xhr.ontimeout = expirar;
    xhr.onabort = () => {
      terminar();
      reject(signal?.reason ?? new DOMException('Upload cancelado.', 'AbortError'));
    };

    try {
      xhr.open('PUT', destino.uploadUrl, true);
      Object.entries(destino.headers).forEach(([header, valor]) => {
        xhr.setRequestHeader(header, valor);
      });
      renovarPrazo();
      xhr.send(arquivo);
    } catch (erro) {
      terminar();
      reject(erro);
    }
  });
